/**
 * PlanningList — agenda/list view of scheduled items over the range, grouped
 * by day and sorted by time. Click a row to edit.
 */
import { useMemo } from 'react';
import {
  type PlannerResource,
  type PlannerService,
  type PlannerScheduledItem,
  type PlannerTaxonomies,
} from '@/services/api';
import { hm, stateInfo, STATE_COLORS } from './plannerShared';

export default function PlanningList({ items, resources, services, tax, uncoveredItemIds, onEdit }: {
  items: PlannerScheduledItem[];
  resources: PlannerResource[];
  services: PlannerService[];
  tax: PlannerTaxonomies | null;
  uncoveredItemIds?: Set<string>;
  onEdit: (item: PlannerScheduledItem) => void;
}) {
  const resName = (id: string) => resources.find((r) => r._id === id)?.name || '—';
  const svcName = (id?: string | null) => services.find((s) => s._id === id)?.name || '—';

  const groups = useMemo(() => {
    const byDay = new Map<string, PlannerScheduledItem[]>();
    for (const it of [...items].sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime())) {
      const d = new Date(it.start);
      const key = d.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' });
      (byDay.get(key) || byDay.set(key, []).get(key)!).push(it);
    }
    return Array.from(byDay.entries());
  }, [items]);

  if (items.length === 0) {
    return <p className="text-sm text-gray-500">No hay planificaciones en este periodo.</p>;
  }

  return (
    <div className="space-y-5">
      {groups.map(([day, dayItems]) => (
        <div key={day}>
          <h3 className="text-sm font-semibold text-gray-700 capitalize mb-2">{day}</h3>
          <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
            {dayItems.map((it) => {
              const st = stateInfo(tax, it.stateNodeId);
              const uncovered = uncoveredItemIds?.has(it._id) || false;
              const color = uncovered ? 'bg-amber-500' : (st && STATE_COLORS[st.id]) || 'bg-vida-600';
              return (
                <button key={it._id} onClick={() => onEdit(it)}
                  className={`w-full text-left flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 ${uncovered ? 'bg-amber-50/60' : ''}`}
                  title={uncovered ? 'El empleado asignado no tiene presencia planificada para este horario' : undefined}>
                  <span className="text-xs font-mono text-gray-500 w-24 shrink-0">{hm(new Date(it.start))}–{hm(new Date(it.end))}</span>
                  <span className={`w-2 h-2 rounded-full shrink-0 ${color}`} />
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-medium text-gray-900 truncate">{svcName(it.serviceId)}</span>
                    <span className="block text-xs text-gray-500 truncate">
                      {resName(it.resourceId)}{it.clientLabel ? ` · ${it.clientLabel}` : ''}
                      {uncovered && <span className="ml-2 text-amber-700">⚠ Sin presencia asignada</span>}
                    </span>
                  </span>
                  {st && <span className="text-xs text-gray-400 shrink-0">{st.name}</span>}
                  {it.alarmAt && <span className="text-[10px] text-amber-600 shrink-0">⏰</span>}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
